import { labelWithSpecies } from 'common/utils/species'
import ExpandMore from 'common/components/ExpandMore'
import { ImageFile } from 'common/types/image'
import { fNumber } from 'common/utils/formatNumber'
import { errorMessageByField } from 'common/utils/hook-form'
import { useGetShrimpSalesTypes } from 'modules/shrimpSales/api/queries'
import { ShrimpSales } from 'modules/shrimpSales/api/types'
import { ReactNode, useState } from 'react'
import { Controller, SubmitHandler, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import ModalImage from 'react-modal-image'

import { Box, Collapse, Divider, Grid, InputAdornment, MenuItem, useTheme } from '@mui/material'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import UploadSingleFile from 'components/upload/UploadSingleFile'

import { ShrimpSalesFormDataType } from '.'
import GridTable from '../GridTable'
import { useSpeciesName } from 'modules/pond/api/selector'
import { SpeciesName } from 'modules/pond/constants'

interface SummaryFormProps {
  onSubmit: SubmitHandler<ShrimpSalesFormDataType>
  onDropPhoto: (acceptedFiles: ImageFile[]) => void
  data?: ShrimpSales
  isEdit?: boolean
}

const SummaryForm = ({ onSubmit, data, isEdit, onDropPhoto }: SummaryFormProps) => {
  const theme = useTheme()
  const { t } = useTranslation('shrimpSales')
  const { data: shrimpSalesTypes } = useGetShrimpSalesTypes()
  const [expanded, setExpanded] = useState(false)
  const speciesName = useSpeciesName()

  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
  } = useFormContext<ShrimpSalesFormDataType>()

  const getShrimpSalesTypeName = (id: number) => shrimpSalesTypes?.find((el) => el.id === id)?.type

  const getController = (name: string, placeholder: string, endAdornment?: ReactNode) => {
    return (
      <Controller
        name={name as keyof ShrimpSalesFormDataType}
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            placeholder={placeholder}
            error={!!errors[name as keyof ShrimpSalesFormDataType]}
            helperText={errorMessageByField(name, errors)}
            size="small"
            sx={{
              minWidth: '50%',
            }}
            InputProps={{
              endAdornment: <InputAdornment position="end">{endAdornment}</InputAdornment>,
            }}
          />
        )}
      />
    )
  }

  const shrimpTypeDropdownController = (
    <Controller
      name="shrimpTypeId"
      control={control}
      render={({ field }) => (
        <TextField
          {...field}
          select
          error={!!errors.shrimpTypeId}
          helperText={errorMessageByField('shrimpTypeId', errors)}
          size="small"
          fullWidth
          sx={{ minWidth: '150px', maxWidth: '50%' }}
          SelectProps={{
            displayEmpty: true,
          }}
        >
          <MenuItem value={-1} disabled>
            {t('choose.type')}
          </MenuItem>
          {Object.values(shrimpSalesTypes ?? []).map((el) => (
            <MenuItem key={el.id} value={el.id}>
              {el.type}
            </MenuItem>
          ))}
        </TextField>
      )}
    />
  )

  const noteController = (
    <Controller
      name="note"
      control={control}
      render={({ field }) => (
        <TextField
          {...field}
          multiline={true}
          minRows={3}
          placeholder={t('note.placeholder')}
          error={!!errors.note}
          helperText={errorMessageByField('note', errors)}
          fullWidth
          size="small"
        />
      )}
    />
  )

  return (
    <form
      id={`save-shrimp-sales-${data?.id}`}
      onSubmit={handleSubmit(onSubmit)}
      key={`shrimp-sales-edit-form-${data?.id}`}
    >
      <Grid container p={2} spacing={2}>
        <Grid item xs={6}>
          <Stack spacing={1}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography fontWeight={600}>{t('buyer')}</Typography>
              {isEdit ? (
                getController('buyer', t('buyer.placeholder'))
              ) : (
                <Typography>{data?.buyerName || '-'}</Typography>
              )}
            </Stack>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography fontWeight={600}>{t('shrimp.type')}</Typography>
              {isEdit ? (
                shrimpTypeDropdownController
              ) : (
                <Typography>
                  {data ? getShrimpSalesTypeName(data.shrimpSalesTypeId) : '-'}
                </Typography>
              )}
            </Stack>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography fontWeight={600}>{labelWithSpecies(t('size'), speciesName)}</Typography>
              <Stack direction="row" spacing={2}>
                {isEdit ? (
                  getController(
                    'shrimpPerKg',
                    t('amount.placeholder'),
                    <Typography color={theme.palette.grey[500]}>{speciesName === SpeciesName.Fish ? t('gram.per.one') : t('shrimp.per.kg')}</Typography>
                  )
                ) : (
                  <>
                    <Typography>{data ? fNumber(data.shrimpCountPerKg) : '-'}</Typography>
                    <Typography color={theme.palette.grey[500]}>{speciesName === SpeciesName.Fish ? t('gram.per.one') : t('shrimp.per.kg')}</Typography>
                  </>
                )}
              </Stack>
            </Stack>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography fontWeight={600}>{t('amount')}</Typography>
              <Stack direction="row" spacing={2}>
                <Typography>{data ? fNumber(data.totalQuantityInKg) : '-'}</Typography>
                <Typography color={theme.palette.grey[500]}>{t('kg')}</Typography>
              </Stack>
            </Stack>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography fontWeight={600}>{t('price')}</Typography>
              <Stack direction="row" spacing={2}>
                <Typography>{data ? fNumber(data.totalRevenue) : '-'}</Typography>
                <Typography color={theme.palette.grey[500]}>{t('baht')}</Typography>
              </Stack>
            </Stack>
            <Stack spacing={1}>
              <Typography fontWeight={600}>{t('note')}</Typography>
              {isEdit ? (
                noteController
              ) : (
                <Typography variant="subtitle1" color={theme.palette.grey[500]}>
                  {data?.note || '-'}
                </Typography>
              )}
            </Stack>
          </Stack>
        </Grid>
        <Divider orientation="vertical" flexItem sx={{ ml: 2 }} />
        <Grid item xs={5}>
          <Stack spacing={1}>
            <Typography fontWeight={600}>{t('photo')}</Typography>
            {isEdit ? (
              <UploadSingleFile file={watch('image')} onDrop={onDropPhoto} error={!!errors.image} />
            ) : data?.billPhotoURL ? (
              <Box sx={{ borderRadius: 2, overflow: 'hidden', width: '128px' }}>
                <ModalImage
                  small={data.billPhotoURL}
                  large={data.billPhotoURL}
                  alt={`bill-${data.buyerName}-${data.id}`}
                />
              </Box>
            ) : (
              <Typography color={theme.palette.grey[500]}>{t('no.photo')}</Typography>
            )}
          </Stack>
        </Grid>
      </Grid>

      <Stack
        direction="row"
        p={2}
        sx={{ cursor: 'pointer' }}
        onClick={() => setExpanded(!expanded)}
      >
        <Typography fontWeight={600}>{t('more.detail')}</Typography>
        <ExpandMore expand={expanded} />
      </Stack>
      <Collapse in={expanded}>
        <GridTable isEdit={isEdit} />
      </Collapse>
    </form>
  )
}

export default SummaryForm
